import { Box, MenuItem, Skeleton, Typography } from "@mui/material";
import { Track } from "../../../types";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import RentalTerms from "./rental-terms";
import FinancialDetails from "./financial-details";
import ListingEmptyState from "../common/empty-state";
import { useGetActiveListing } from "../../../hooks/listings.hook";
import EditRentalListingModal from "./rental-listing-forms/edit-rental-listing";
import PublishRentalListing from "./publish-rental-listing/publish-rental-listing";
import { Pencil } from "lucide-react";
import { useState } from "react";
import CheckItem from "../common/check-item";
import { ListingType } from "@/modules/property-tracks/dtos";
import ListingTopBar from "../common/top-bar";
import ListingMedia from "../common/listing-media";
import AddRentalListing from "./rental-listing-forms/add-rental-listing";
import ViewListing from "./view-listing";
import UnpublishListingMenuItem from "../common/menu-items/unpublish-listing-item";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";

export default function RentalActiveListingTab({ track }: { track: Track }) {
  const [editRentalListingModalOpen, setEditRentalListingModalOpen] =
    useState(false);

  const { data, isLoading } = useGetActiveListing({
    params: {
      propertyUid: track.property.uid,
      listingType: ListingType.RENTAL,
    },
  });

  if (isLoading) return <Skeleton />;

  if (!data)
    return (
      <ListingEmptyState callToAction={<AddRentalListing track={track} />} />
    );

    const { readonly } = useReadonlyContext();

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        gap: t.nawy.spacing.md,
      })}
    >
      {/* TOP BAR */}
      <ListingTopBar
        listing={data}
        actions={
          <>
            <PublishRentalListing
              listing={data}
              trackUid={track.uid}
              propertyUid={track.property.uid}
            />
            <MoreActionsMenu>
              <MenuItem
              disabled={readonly}
                sx={(theme) => ({
                  display: "flex",
                  gap: theme.nawy.spacing.xs,
                })}
                onClick={() => setEditRentalListingModalOpen(true)}
              >
                <Pencil />
                Edit Listing
              </MenuItem>
              {data.inventoryListingId && (
                <UnpublishListingMenuItem listingUid={data.uid} />
              )}
            </MoreActionsMenu>
          </>
        }
      />
      <Box
        sx={(theme) => ({
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          p: theme.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Is this property rented?
          </Typography>
        </Box>

        <CheckItem text="Rented" isTrue={data.isRented} />

        <ViewListing inventoryListingId={data.inventoryListingId} />
      </Box>

      <RentalTerms listing={data} />
      <FinancialDetails listing={data} />
      <ListingMedia title="Listing Media" documents={data.trackDocuments} />
      <EditRentalListingModal
        listing={data}
        open={editRentalListingModalOpen}
        trackUid={track.uid}
        propertyUid={track.property.uid}
        handleOpen={() => setEditRentalListingModalOpen(true)}
        handleClose={() => setEditRentalListingModalOpen(false)}
      />
    </Box>
  );
}
